import React, { useEffect, useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import ConfirmModal from "../../components/common/ConfirmModal";
import PageHeader from "../../components/common/PageHeader";
import DataTable from "../../components/common/DataTable";
import StatusChip from "../../components/common/StatusChip";

import { USER_LIST_STRINGS, COMMON_STRINGS } from "../../constants/strings";

import { getUsers, updateUserStatus } from "../../services/api";

import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

// 🔥 TYPES
import { UserRow, StatusType, UserStatus } from "../../types";

// 🔥 Utils
import { getUserImage, getUserInitial, getAvatarColor } from "../../utils/user";

import "../table-common.css";

const UserListPage = () => {
  const dispatch = useDispatch();

  const [rows, setRows] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<UserRow["status"] | "">("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // ================= FETCH =================
  const fetchUsers = async () => {
    try {
      setLoading(true);

      const res = await getUsers();
      console.log(res);

      const formatted: UserRow[] = (res.data.data || []).map((user: any) => ({
        _id: user._id,
        name: user.name,
        mobile: user.mobile,
        city: user.city,
        state: user.state,
        pincode: user.pincode,
        houseNo: user.houseNo,
        profileImage: user.profileImage,
        status: user.isBlocked ? "blocked" : "active",
      }));

      setRows([...formatted].reverse());
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // ================= UPDATE =================
  const handleStatusChange = async (
    id: string,
    newStatus: UserRow["status"],
  ) => {
    try {
      setRows((prev) =>
        prev.map((row) =>
          row._id === id ? { ...row, status: newStatus } : row,
        ),
      );

      await updateUserStatus(id);

      dispatch(
        showSnackbar({
          message: "User status updated successfully",
          type: "success",
        }),
      );
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );

      fetchUsers();
    }
  };

  const handleConfirmBlock = () => {
    if (selectedId) {
      handleStatusChange(selectedId, "blocked");
    }
    setConfirmOpen(false);
  };

  // ================= FILTER =================
  const filteredRows = rows.filter((row) => {
    const search = searchText.toLowerCase();

    const searchMatch =
      row.name?.toLowerCase().includes(search) ||
      row.mobile?.includes(searchText);

    const statusMatch = statusFilter ? row.status === statusFilter : true;

    return searchMatch && statusMatch;
  });

  // ================= COLUMNS =================
  const columns: GridColDef<UserRow>[] = [
    {
      field: "thumbnail",
      headerName: "Thumbnail",
      width: 90,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",

      renderCell: (params) => {
        const user = params.row;

        const imageUrl = getUserImage(user.profileImage);
        const firstChar = getUserInitial(user.name, user.mobile);
        const bgColor = getAvatarColor(firstChar);

        return (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: imageUrl ? "transparent" : bgColor,
              color: "#fff",
              fontWeight: 600,
              fontSize: "16px",
              overflow: "hidden",
            }}
          >
            {imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt={user.name}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              firstChar
            )}
          </Box>
        );
      },
    },

    {
      field: "name",
      headerName: USER_LIST_STRINGS.TITLE,
      flex: 1,
      renderCell: (params) => params.row.name || "N/A",
    },

    {
      field: "mobile",
      headerName: USER_LIST_STRINGS.PHONE,
      flex: 1,
    },

    {
      field: "address",
      headerName: USER_LIST_STRINGS.ADDRESS,
      flex: 2,
      renderCell: (params) => {
        const user = params.row;

        return (
          [user.houseNo, user.city, user.state, user.pincode]
            .filter(Boolean)
            .join(", ") || "N/A"
        );
      },
    },

    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => <StatusChip status={params.value} />,
    },

    {
      field: "actions",
      headerName: "Actions",
      flex: 1.5,
      sortable: false,
      align: "center",
      headerAlign: "center",

      renderCell: (params) => {
        const currentStatus = params.row.status;

        return (
          <Box className="action-cell">
            <Stack direction="row" spacing={1}>
              {currentStatus !== "blocked" && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<BlockIcon />}
                  onClick={() => {
                    setSelectedId(params.row._id);
                    setConfirmOpen(true);
                  }}
                  sx={{
                    color: "var(--danger)",
                    borderColor: "var(--danger)",
                    textTransform: "none",
                    borderRadius: "8px",
                    "&:hover": {
                      backgroundColor: "var(--danger-soft)",
                    },
                  }}
                >
                  {USER_LIST_STRINGS.BLOCK}
                </Button>
              )}

              {currentStatus === "blocked" && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => handleStatusChange(params.row._id, "active")}
                  sx={{
                    backgroundColor: "var(--success)",
                    textTransform: "none",
                    borderRadius: "8px",
                  }}
                >
                  {USER_LIST_STRINGS.UNBLOCK}
                </Button>
              )}
            </Stack>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <PageHeader
        title={USER_LIST_STRINGS.TITLE}
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={USER_LIST_STRINGS.SEARCH_PLACEHOLDER}
        filterValue={statusFilter}
        onFilterChange={(value) => setStatusFilter(value as UserStatus | "")}
        filterOptions={[
          { label: "All", value: "" },
          { label: USER_LIST_STRINGS.ACTIVE, value: "active" },
          { label: USER_LIST_STRINGS.BLOCKED, value: "blocked" },
        ]}
      />

      <DataTable
        rows={filteredRows}
        columns={columns}
        loading={loading}
        getRowId={(row) => row._id}
        pageSizeOptions={[10, 20, 50]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
      />

      <ConfirmModal
        open={confirmOpen}
        title={USER_LIST_STRINGS.CONFIRM_BLOCK_TITLE}
        description={USER_LIST_STRINGS.CONFIRM_BLOCK_DESC}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmBlock}
      />
    </Box>
  );
};

export default UserListPage;
